/** Data and small helpers for the public pages. Everything here works signed out. */
import { useEffect, useSyncExternalStore } from 'react';
import { useApi } from '../lib/api';
import type { Plan } from '../lib/types';
import type { Facts } from './content';

export interface Site {
  businessName: string;
  supportEmail: string;
  /** The seller's legal name, postal address and court city; empty in development. */
  legalName: string;
  businessAddress: string;
  legalCity: string;
  /** Reddit's ad pixel ('' for none), and whether this visitor must agree before it runs (site/pixel.tsx). */
  redditPixelId: string;
  askConsent: boolean;
}

export interface PublicPlan {
  enabled: boolean;
  plan: Plan | null;
  trialDays: number;
  aiMonthlyMessages: number;
  aiTrialMessages: number;
}

/** Who sells the service (BUSINESS_NAME, SUPPORT_EMAIL on the server). */
export const useSite = () => useApi<Site>('/api/site', { staleTime: Infinity });

/** The live price and limits; `plan` is null while billing is off or Dodo can't be reached. */
export const usePublicPlan = () => useApi<PublicPlan>('/api/billing/plan', { staleTime: 5 * 60_000 });

/** What the shared copy in content.ts needs, with the same defaults the pages show before the data arrives. */
export function useFacts(): Facts {
  const site = useSite().data;
  const plan = usePublicPlan().data;
  return {
    origin: siteOrigin(),
    business: site?.businessName ?? 'RareMargin',
    email: site?.supportEmail ?? '',
    plan: plan?.plan ?? null,
    trialDays: plan?.trialDays ?? 14,
    aiMonthlyMessages: plan?.aiMonthlyMessages ?? 300,
    aiTrialMessages: plan?.aiTrialMessages ?? 50,
    legalName: site?.legalName ?? '',
    address: site?.businessAddress ?? '',
  };
}

/** This site's address. The server renders the public pages (ssr.tsx) with APP_URL, which is the same address. */
let serverOrigin = '';
export const siteOrigin = () => (typeof window === 'undefined' ? serverOrigin : window.location.origin);
export const setServerOrigin = (origin: string) => void (serverOrigin = origin);

/**
 * False on the server and while the browser takes over the server's HTML, then true: for what depends on this browser
 * (stored choices, privacy signals), which the server can't know and so mustn't render.
 */
const never = () => () => {};
export const useHydrated = () => useSyncExternalStore(never, () => true, () => false);

/** Sets the tab title while the page is shown. */
export function useTitle(title: string) {
  useEffect(() => {
    const before = document.title;
    document.title = title;
    return () => {
      document.title = before;
    };
  }, [title]);
}

/**
 * Whether this browser was signed in last time. The session cookie is HTTP-only, so this hint lets `/` show the
 * landing page straight away to visitors, instead of a loading screen while the session check runs.
 */
const HINT = 'raremargin:signed-in';
export const sessionHint = {
  get: () => {
    // Not on the server, which renders the public pages as a visitor (and where Node warns about localStorage).
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem(HINT) === '1';
    } catch {
      return false;
    }
  },
  set: (on: boolean) => {
    try {
      if (on) localStorage.setItem(HINT, '1');
      else localStorage.removeItem(HINT);
    } catch {
      // Storage can be off (private mode); the hint is only an optimisation.
    }
  },
};
